import { Button, type ButtonProps } from "./Button";
import { Icon, type IconSize } from "./Icon";
import { Minus, Plus, TrashSimple, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { SPRING, TRANSITION } from "@/lib/motion";
import { cva } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import {
  forwardRef,
  useState,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from "react";

/** `footer` is the item page footer: `lg` height and glyphs on narrow buttons, so the CTAs beside it fit 390px; `compact` is an option row: 40px targets drawn in a ~72px footprint. */
export type StepperSize = "sm" | "md" | "lg" | "footer" | "compact";
type StepperVariant = "outline" | "soft" | "plain" | "floating" | "split";
type StepperTone = "primary" | "secondary" | "neutral";
type DataAttributes = { [key: `data-${string}`]: string | undefined };

/** Extra attributes for one of the stepper's buttons (test ids, analytics). */
export type StepperButtonAttributes = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick" | "children" | "className" | "aria-label" | "disabled"
> &
  DataAttributes;

const groupVariants = cva("inline-flex shrink-0 items-center", {
  variants: {
    variant: {
      outline: "overflow-hidden rounded-control border border-border bg-card",
      soft: "overflow-hidden rounded-control bg-card-text/5",
      plain: "",
      floating: "overflow-hidden rounded-control border border-border bg-card/95 shadow-raised backdrop-blur-md",
      split: "",
    },
  },
  defaultVariants: { variant: "outline" },
});

const LOOSE_GAP: Record<StepperSize, string> = { sm: "gap-1", md: "gap-2", lg: "gap-3", footer: "gap-2", compact: "" };

const BUTTON_BOX: Record<StepperSize, string> = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-14 w-12",
  footer: "h-14 w-9",
  compact: "h-10 w-10",
};

const COMPACT_GROUP = "-mx-2";
const COMPACT_INNER_EDGE = { decrement: "-mr-2", increment: "-ml-2" } as const;

/** Glyph size of each stepper size; a `removeSlot` trigger uses it so the trash matches the − and +. */
export const QUANTITY_STEPPER_GLYPH: Record<StepperSize, IconSize> = { sm: "xs", md: "md", lg: "lg", footer: "lg", compact: "lg" };
const VALUE_TEXT: Record<StepperSize, string> = {
  sm: "min-w-6 text-sm font-semibold",
  md: "min-w-8 text-base font-semibold",
  lg: "min-w-8 text-lg font-bold",
  footer: "min-w-6 text-lg font-bold",
  compact: "min-w-6 text-base font-semibold",
};

const BUTTON_LOOK: Record<StepperVariant, Pick<ButtonProps, "variant" | "shape">> = {
  outline: { variant: "plain", shape: "square" },
  soft: { variant: "plain", shape: "square" },
  plain: { variant: "plain", shape: "circle" },
  floating: { variant: "plain", shape: "square" },
  split: { variant: "outline", shape: "circle" },
};

export type QuantityStepperProps = {
  value: number;
  onIncrement: (event: MouseEvent<HTMLButtonElement>) => void;
  onDecrement: (event: MouseEvent<HTMLButtonElement>) => void;
  min?: number;
  /** Upper bound; uncapped by default (pass the business cap explicitly). */
  max?: number;
  size?: StepperSize;
  /** outline/soft: one bordered or tinted box; plain: bare glyphs; floating: blurred card over photos; split: separate circles. */
  variant?: StepperVariant;
  tone?: StepperTone;
  /** Overrides the glyph size `size` picks (option quantities: a 24px + in a 40px box). */
  glyphSize?: IconSize;
  /** Glyph color of the decrement button; defaults to `tone`. */
  decrementTone?: StepperTone;
  /** At `min` only the + button shows; the rest grows in with a width motion. */
  collapsible?: boolean;
  /** At `min` the decrement button becomes a trash button calling `onRemove` (or `onDecrement`). */
  removeAtMin?: boolean;
  onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Replaces the trash button at `min` (a dialog trigger that owns its own confirmation). */
  removeSlot?: ReactNode;
  /** Pops the number on change with the press spring. */
  bump?: boolean;
  /** Accessible name of the group, e.g. "Quantidade de Pizza grande". */
  label?: string;
  incrementLabel?: string;
  decrementLabel?: string;
  removeLabel?: string;
  disabled?: boolean;
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
  incrementProps?: StepperButtonAttributes;
  decrementProps?: StepperButtonAttributes;
  valueProps?: DataAttributes;
  className?: string;
};

function StepButton({
  glyph,
  glyphSize,
  size,
  variant,
  tone,
  label,
  disabled,
  onClick,
  extra,
  edge,
}: {
  glyph: IconGlyph;
  glyphSize: IconSize;
  size: StepperSize;
  variant: StepperVariant;
  tone: Exclude<ButtonProps["tone"], "game" | "night">;
  label: string;
  disabled: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  extra?: StepperButtonAttributes;
  edge?: string;
}) {
  return (
    <Button
      {...extra}
      {...BUTTON_LOOK[variant]}
      tone={tone}
      size="none"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn("shrink-0 p-0", BUTTON_BOX[size], edge)}
    >
      <Icon as={glyph} size={glyphSize} weight="bold" />
    </Button>
  );
}

/** Quantity control (− value +) for the sacola, item page, options and rewards; one primitive, five looks. */
export const QuantityStepper = forwardRef<HTMLDivElement, QuantityStepperProps>(function QuantityStepper(
  {
    value,
    onIncrement,
    onDecrement,
    min = 0,
    max = Infinity,
    size = "md",
    variant = "outline",
    tone = "primary",
    glyphSize: glyphSizeProp,
    decrementTone,
    collapsible = false,
    removeAtMin = false,
    onRemove,
    removeSlot,
    bump = false,
    label,
    incrementLabel = "Aumentar quantidade",
    decrementLabel = "Diminuir quantidade",
    removeLabel = "Remover item",
    disabled = false,
    incrementDisabled = false,
    decrementDisabled = false,
    incrementProps,
    decrementProps,
    valueProps,
    className,
  },
  ref,
) {
  const [previous, setPrevious] = useState(value);
  const [direction, setDirection] = useState<1 | -1>(1);
  if (value !== previous) {
    setPrevious(value);
    setDirection(value > previous ? 1 : -1);
  }

  const glyphSize = glyphSizeProp ?? QUANTITY_STEPPER_GLYPH[size];
  const atMin = value <= min;
  const collapsed = collapsible && atMin;
  const removing = removeAtMin && atMin;
  const gap = variant === "plain" || variant === "split" ? LOOSE_GAP[size] : "";
  const compact = size === "compact";

  const decrement = removing ? (
    (removeSlot ?? (
      <StepButton
        glyph={TrashSimple}
        glyphSize={glyphSize}
        size={size}
        variant={variant}
        tone="danger"
        label={removeLabel}
        disabled={disabled || decrementDisabled}
        onClick={onRemove ?? onDecrement}
        extra={decrementProps}
        edge={compact ? COMPACT_INNER_EDGE.decrement : undefined}
      />
    ))
  ) : (
    <StepButton
      glyph={Minus}
      glyphSize={glyphSize}
      size={size}
      variant={variant}
      tone={decrementTone ?? tone}
      label={decrementLabel}
      disabled={disabled || decrementDisabled || atMin}
      onClick={onDecrement}
      extra={decrementProps}
      edge={compact ? COMPACT_INNER_EDGE.decrement : undefined}
    />
  );

  const number = (
    <span
      {...valueProps}
      aria-live="polite"
      aria-atomic
      className={cn("inline-flex justify-center text-center tabular-nums leading-none text-card-text", VALUE_TEXT[size])}
    >
      {bump ? (
        <motion.span
          key={value}
          initial={{ scale: direction > 0 ? 1.35 : 0.75 }}
          animate={{ scale: 1 }}
          transition={SPRING.wobble}
          className="inline-block"
        >
          {value}
        </motion.span>
      ) : (
        value
      )}
    </span>
  );

  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={cn(groupVariants({ variant }), gap, compact && !collapsed && COMPACT_GROUP, className)}
    >
      {collapsible ? (
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              key="decrement"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "auto", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={TRANSITION.size}
              className={cn("flex items-center overflow-hidden", gap)}
            >
              {decrement}
              {number}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <>
          {decrement}
          {number}
        </>
      )}
      <StepButton
        glyph={Plus}
        glyphSize={glyphSize}
        size={size}
        variant={variant}
        tone={tone}
        label={incrementLabel}
        disabled={disabled || incrementDisabled || value >= max}
        onClick={onIncrement}
        extra={incrementProps}
        edge={compact && !collapsed ? COMPACT_INNER_EDGE.increment : undefined}
      />
    </div>
  );
});
